import { FC, ReactNode, useEffect, useState, useRef, MouseEvent, useMemo } from 'react';
import DefaultLayout from '@/layouts/Default/default';
import { AnimatePresence, motion } from 'framer-motion';
import TurboWordmark from '@/components/Shared/TurboWordmark';
import meltingMetalVideo from '@/assets/video/meltingmetal.mp4';
import narrativeVideo from '@/assets/video/narrative.mp4';
import storyImage from '@/assets/stock/IMG_2888.jpg';
import ServicesShowcaseSection from '@/components/Landing/ServicesShowcaseSection';
import InteractiveServicesSection from '@/components/Landing/InteractiveServicesSection';
import TimelineSection from '@/components/Landing/TimelineSection';
import { Seo } from '@/components/SEO/Seo';
import { pageMetadata } from '@/config/pageMetadata';
import { siteMetadata } from '@/config/seo';

const badge = (t: string) => (
  <span className="h-10 w-10 inline-flex items-center justify-center rounded-xl bg-white text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100 border border-zinc-200 dark:border-zinc-700 shadow-sm">
    {t}
  </span>
);

const ServiceCard: FC<{ code: string; title: string; text: string; subtitle?: string; footnote?: string }> = ({
  code,
  title,
  text,
  subtitle,
  footnote,
}) => (
  <a className="group relative overflow-hidden rounded-3xl bg-zinc-100 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100 border border-zinc-200 dark:border-zinc-700 shadow-sm flex flex-col p-6 md:p-7">
    <div className="flex items-center gap-4">
      {badge(code)}
      <div className="text-lg md:text-xl font-light leading-tight">{title}</div>
      <svg className="ml-auto h-9 w-9 rounded-full bg-zinc-200/70 dark:bg-zinc-700/70 p-2 text-zinc-700 dark:text-zinc-200" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M7 17L17 7" />
        <path d="M7 7h10v10" />
      </svg>
    </div>
    {subtitle && (
      <p className="mt-3 text-xs uppercase tracking-[0.28em] text-zinc-500 dark:text-zinc-400">
        {subtitle}
      </p>
    )}
    <p className="mt-4 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">
      {text}
      {footnote && (
        <>
          {' '}
          <span className="font-medium text-zinc-800 dark:text-zinc-200">
            {footnote}
          </span>
        </>
      )}
    </p>
  </a>
);

type HeroSlide = {
  id: string;
  title: ReactNode;
  description: ReactNode;
  ctaLabel: string;
  ctaHref: string;
  kicker?: ReactNode;
  background: 'video' | 'gradient' | 'image';
  backgroundClass?: string;
  videoSrc?: string;
  videoOverlayClass?: string;
  imageSrc?: string;
  imageOverlayClass?: string;
  blurBackdropClass?: string;
  blurGradientClass?: string;
  blurMask?: string;
  startAtSeconds?: number;
};

const heroSlides: HeroSlide[] = [
  {
    id: 'slingshot-2026',
    kicker: 'Slingshot 2026',
    title: (
      <>
        Onshore dev pods that are 8X faster while elevating quality.
        <br className="hidden sm:block" />
        Five partner slots for next year.
      </>
    ),
    description:
      'Agentic automation rails pair with senior product+engineering pods to ship production software in days—no trade-off between speed, scope, or quality.',
    ctaLabel: 'Claim a 2026 slot',
    ctaHref: '/contact?topic=slingshot',
    background: 'video',
    videoSrc: narrativeVideo,
    videoOverlayClass: 'bg-zinc-900/35',
    blurBackdropClass: 'backdrop-blur-[10px] backdrop-saturate-[1.05]',
    blurGradientClass: 'bg-transparent',
    blurMask: 'linear-gradient(to top, rgba(0,0,0,1) 22%, rgba(0,0,0,0) 78%)',
    startAtSeconds: 5,
  },
  {
    id: 'ai-eye-opener',
    kicker: 'Course Launch',
    title: 'What AI Can Actually Do',
    description:
      'Our five-step AI Eye-Opener shows teams how to move from buzzwords to production-grade automation in weeks.',
    ctaLabel: 'Preview the course',
    ctaHref: '/blog?category=ai',
    background: 'video',
    videoSrc: '/videos/drivin.mp4',
    videoOverlayClass: 'bg-transparent',
    blurBackdropClass: 'backdrop-blur-[10px] backdrop-saturate-[1.05]',
    blurGradientClass: 'bg-transparent',
    blurMask: 'linear-gradient(to top, rgba(0,0,0,1) 18%, rgba(0,0,0,0) 70%)',
  },
  {
    id: 'our-story',
    kicker: 'Inside Turbo',
    title: 'Our Story Is Different',
    description: 'We built an operating system that compounds speed, craft, and outcomes—see how it works.',
    ctaLabel: 'Read the story',
    ctaHref: '/about',
    background: 'image',
    imageSrc: storyImage,
    imageOverlayClass: 'bg-transparent',
    blurBackdropClass: 'backdrop-blur-[7px] backdrop-saturate-[1] backdrop-contrast-[0.98]',
    blurGradientClass: 'bg-transparent',
    blurMask: 'linear-gradient(to top, rgba(0,0,0,1) 22%, rgba(0,0,0,0) 80%)',
  },
  {
    id: 'year-one',
    kicker: 'Milestone',
    title: 'Turbo Labs Celebrates Its First Year',
    description:
      '1,100 product features shipped, a team of seven—we are proven and ready to go to market with our repeated craft.',
    ctaLabel: 'See the momentum',
    ctaHref: '/about#year-one',
    background: 'video',
    videoSrc: '/videos/red.mp4',
    videoOverlayClass: 'bg-transparent',
    blurBackdropClass: 'backdrop-blur-[10px] backdrop-saturate-[1.05]',
    blurGradientClass: 'bg-transparent',
    blurMask: 'linear-gradient(to top, rgba(0,0,0,1) 18%, rgba(0,0,0,0) 70%)',
  },
];

const whyTurboProofs = [
  {
    code: '01',
    title: '10× speed without trade-offs',
    subtitle: 'Automation at the core',
    text: 'Automation rails, agents, and expert crews ship releases in days instead of quarters while revenue climbs and costs fall.',
    footnote: 'Speed works in every industry.',
  },
  {
    code: '02',
    title: 'Real AI impact, no bubble',
    subtitle: 'Proof over hype',
    text: 'We help teams find lasting automation wins tied to revenue and cost, not slideware hype cycles.',
    footnote: 'Skip the bubble. Keep the upside.',
  },
  {
    code: '03',
    title: 'Productized customer experience',
    subtitle: 'Ether at work',
    text: 'Daily drips, live scope and cost, and documentation that updates itself as we build together.',
    footnote: 'Project management built for partners.',
  },
  {
    code: '04',
    title: 'Future advantage today',
    subtitle: '18-month head start',
    text: 'The agent ecosystems others will rent in two years are already live in your stack with us.',
    footnote: 'Be the reference case, not the follower.',
  },
  {
    code: '05',
    title: 'Competition-proof velocity',
    subtitle: 'Impossible delivered',
    text: 'We help you reach for the impossible at speed, leaving competitors unable to keep pace.',
    footnote: 'Your moat compounds every sprint.',
  },
  {
    code: '06',
    title: 'Insane quality through deep expertise',
    subtitle: 'Craft at scale',
    text: 'Design, engineering, and automation leads turn brutal workflows into products leaders trust.',
    footnote: 'Complexity → clarity every sprint.',
  },
];

const LeftRail: FC = () => {
  const stats = [
    { label: 'Features shipped', value: '1.1K', caption: '2025 velocity ledger' },
    { label: 'Development speed', value: '8×', caption: 'Faster than industry norm' },
    { label: 'Active projects', value: '12', caption: 'In flight this quarter' },
    { label: 'Raving fans', value: '6', caption: 'Clients expanding with us' },
  ];
  const [activeStat, setActiveStat] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setActiveStat((prev) => (prev + 1) % stats.length);
    }, 4200);
    return () => window.clearInterval(id);
  }, [stats.length]);

  const current = stats[activeStat];

  return (
    <aside className="hidden w-[220px] shrink-0 md:flex lg:w-[260px] xl:w-[300px]">
      <div className="sticky top-0 flex h-screen w-full flex-col justify-between px-6 pt-16 md:pt-24 pb-12">
        <TurboWordmark className="h-7 w-auto self-start text-zinc-900 dark:text-zinc-100" />
        <div className="flex w-full flex-col gap-3">
          <div className="w-full rounded-2xl ring-1 ring-black/10 dark:ring-white/10 bg-white/85 dark:bg-zinc-900/85 backdrop-blur-xl shadow-md px-6 py-8">
            <motion.div
              key={current.label}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              className="space-y-3"
            >
              <div className="text-6xl font-light text-zinc-900 dark:text-zinc-100">
                {current.value}
              </div>
              <div className="text-base font-medium text-zinc-700 dark:text-zinc-200">
                {current.label}
              </div>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 max-w-[200px] leading-snug">
                {current.caption}
              </p>
            </motion.div>
            <div className="mt-6 flex gap-2">
              {stats.map((_, index) => (
                <motion.span
                  key={index}
                  className="h-1.5 w-1.5 rounded-full bg-zinc-400/60 dark:bg-zinc-600/60"
                  animate={{ opacity: index === activeStat ? 1 : 0.3, scale: index === activeStat ? 1.2 : 1 }}
                  transition={{ duration: 0.2 }}
                />
              ))}
            </div>
          </div>
        <div className="w-full rounded-2xl ring-1 ring-black/10 dark:ring-white/10 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-xl shadow-md px-5 py-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-600 dark:text-zinc-400 mb-1">What you've missed</div>
          <div className="text-xs text-zinc-900 dark:text-zinc-100">Recent case + news highlights</div>
        </div>
      </div>
    </div>
    </aside>
  );
};

// Site nav moved to components/Shared/SiteNav and rendered globally

const LandingPage: FC = () => {
  const seo = pageMetadata.home;
  const landingStructuredData = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: siteMetadata.siteName,
        url: siteMetadata.siteUrl,
        description: seo.description ?? siteMetadata.defaultDescription,
        inLanguage: 'en',
      },
    ],
    [seo.description],
  );
  const [activeSlide, setActiveSlide] = useState(0);
  const timelineRef = useRef<HTMLDivElement>(null);

  const deliveryTimeline = [
    {
      day: 0,
      title: 'Kickoff & intake',
      description:
        'Share your workflow, artifacts, and targets with us. We map the context immediately and prime the daily drip cadence.',
    },
    {
      day: 1,
      title: 'Scope + wireframes in Ether',
      description:
        'On Day 1 you receive the full scope, clickable wireframes, and assumptions directly in Ether ready for sign-off.',
    },
    {
      day: 1,
      title: 'Approval → OBJECTS + workflows captured',
      description:
        'Once you approve, the product team documents every OBJECT, workflow, and automation edge so build starts with zero ambiguity.',
    },
    {
      day: 2,
      title: 'Parallel build in daily drips',
      description:
        'Design, engineering, and automation run in parallel, shipping production-ready increments in the daily drip updates you see in Ether.',
    },
    {
      day: 3,
      title: 'Feedback, iterate, go again',
      description:
        'You review each drop, we apply feedback within a day or two, and the next cycle starts—the pace is set entirely by how fast you respond.',
    },
  ];

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setActiveSlide((prev) => (prev + 1) % heroSlides.length);
    }, 12000);
    return () => window.clearTimeout(timer);
  }, [activeSlide]);

  const currentSlide = heroSlides[activeSlide];

  const handleCtaClick = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!href.startsWith('/contact')) return;
    event.preventDefault();
    // @ts-expect-error custom global event handled by layout drawer
    window.dispatchEvent(new Event('open-contact'));
  };

  return (
    <DefaultLayout hideHeader>
      <Seo {...seo} structuredData={landingStructuredData} />
      <div className="min-h-screen w-full bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100">
        <div className="flex flex-col md:flex-row">
          <LeftRail />
          <main className="flex-1 px-4 sm:px-6 lg:px-10 xl:px-14 2xl:px-20 pt-24 md:pt-20 lg:pt-16 xl:pt-0">
            <div className="w-full max-w-[1920px]">
              {/* Hero */}
              <section className="pt-6 sm:pt-10 md:pt-24 pb-8">
                <div className="relative overflow-hidden rounded-[24px] border border-zinc-200 dark:border-zinc-800 shadow-sm min-h-[320px] h-[36vw] max-h-[480px] bg-gradient-to-br from-amber-500/35 via-zinc-900 to-emerald-500/35 dark:from-amber-500/25 dark:via-zinc-950 dark:to-emerald-500/25">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={`bg-${currentSlide.id}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.6, ease: 'easeOut' }}
                      className="absolute inset-0"
                    >
                      {currentSlide.background === 'video' ? (
                        <>
                          <video
                            key={currentSlide.id}
                            className="absolute -inset-px block h-[calc(100%+2px)] w-[calc(100%+2px)] object-cover will-change-transform transform-gpu"
                            autoPlay
                            loop
                            muted
                            playsInline
                            onLoadedMetadata={(event) => {
                              const startAt = currentSlide.startAtSeconds ?? 0;
                              if (startAt > 0) {
                                try {
                                  event.currentTarget.currentTime = startAt;
                                } catch (error) {
                                  // Ignore seek errors (mobile Safari can block programmatic seeks before play starts)
                                }
                              }
                            }}
                            onTimeUpdate={(event) => {
                              const startAt = currentSlide.startAtSeconds ?? 0;
                              if (startAt > 0 && event.currentTarget.currentTime < startAt - 0.05) {
                                event.currentTarget.currentTime = startAt;
                              }
                            }}
                          >
                            <source
                              src={currentSlide.videoSrc || meltingMetalVideo}
                              type="video/mp4"
                            />
                          </video>
                          <div
                            aria-hidden
                            className={`pointer-events-none absolute inset-0 ${
                              currentSlide.videoOverlayClass || 'bg-transparent'
                            }`}
                          />
                        </>
                      ) : currentSlide.background === 'image' ? (
                        <>
                          <img
                            src={currentSlide.imageSrc || ''}
                            alt=""
                            className="absolute -inset-px block h-[calc(100%+2px)] w-[calc(100%+2px)] object-cover"
                            loading="lazy"
                          />
                          <div
                            aria-hidden
                            className={`pointer-events-none absolute inset-0 ${
                              currentSlide.imageOverlayClass || 'bg-transparent'
                            }`}
                          />
                        </>
                      ) : (
                        <div
                          className={`h-full w-full ${currentSlide.backgroundClass || ''}`}
                          aria-hidden="true"
                        />
                      )}
                    </motion.div>
                  </AnimatePresence>
                  <div className="pointer-events-none absolute inset-0" />
                  <div
                    className={`pointer-events-none absolute inset-0 ${
                      currentSlide.blurGradientClass || 'bg-transparent'
                    } ${
                      currentSlide.blurBackdropClass ||
                      'backdrop-blur-[8px] backdrop-saturate-[1] backdrop-contrast-[1]'
                    }`}
                    style={{
                      WebkitMaskImage:
                        currentSlide.blurMask ||
                        'linear-gradient(to top, rgba(0,0,0,1) 20%, rgba(0,0,0,0) 78%)',
                      maskImage:
                        currentSlide.blurMask ||
                        'linear-gradient(to top, rgba(0,0,0,1) 20%, rgba(0,0,0,0) 78%)',
                      WebkitMaskSize: '100% 100%',
                      maskSize: '100% 100%',
                      WebkitMaskRepeat: 'no-repeat',
                      maskRepeat: 'no-repeat',
                    }}
                  />

                  <div className="absolute top-5 left-5 z-[30] flex gap-2">
                    {heroSlides.map((slide, index) => (
                      <button
                        key={slide.id}
                        type="button"
                        onClick={() => setActiveSlide(index)}
                        className={`h-2.5 w-2.5 rounded-full border border-white/50 transition-all ${
                          index === activeSlide ? 'bg-white shadow-[0_0_0_4px_rgba(255,255,255,0.15)]' : 'bg-white/30 hover:bg-white/60'
                        }`}
                        aria-label={`Show slide ${index + 1}`}
                      />
                    ))}
                  </div>

                  <AnimatePresence mode="wait">
                    <motion.div
                      key={`copy-${currentSlide.id}`}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -16 }}
                      transition={{ duration: 0.45, ease: 'easeOut' }}
                      className="absolute inset-0 z-[20] flex flex-col justify-end gap-4 p-5 md:p-8"
                    >
                      {currentSlide.kicker && (
                        <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/80">
                          {currentSlide.kicker}
                        </div>
                      )}
                      <motion.h1
                        layout
                        className="font-creato font-light text-white drop-shadow-md text-[clamp(28px,3.6vw,42px)] leading-tight"
                      >
                        {currentSlide.title}
                      </motion.h1>
                      <div className="max-w-2xl text-white/85 text-sm md:text-base leading-relaxed">
                        {currentSlide.description}
                      </div>
                      <div>
                        <a
                          href={currentSlide.ctaHref}
                          onClick={(event) => handleCtaClick(event, currentSlide.ctaHref)}
                          className="inline-flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-sm font-medium text-zinc-900 transition-colors hover:bg-white"
                        >
                          {currentSlide.ctaLabel}
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M7 17L17 7" />
                            <path d="M7 7h10v10" />
                          </svg>
                        </a>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </section>

              {/* Hero companion copy (clean, single line, decorative icons) */}
              <section className="pb-12 md:pb-16">
                <div className="rounded-[24px] border border-zinc-200 bg-white/90 px-6 py-10 shadow-sm dark:border-zinc-800 dark:bg-zinc-900/85 md:px-10 md:py-14">
                  <p className="mb-4 text-xs font-semibold uppercase tracking-[0.24em] text-zinc-500 dark:text-zinc-400">
                    TLDR
                  </p>
                  <h2 className="text-[clamp(26px,3vw,40px)] font-creato leading-[1.18] text-zinc-900 dark:text-zinc-50">
                    turbo labs, inc. uses proven futuristic methods of building to enable tech companies to destroy their competition by speed to ship and raising the bar of what's possible.
                  </h2>
                  <p className="mt-4 text-base md:text-lg leading-relaxed text-zinc-700 dark:text-zinc-300">
                    we are on a mission to bring these methods of operations outside of tech for companies that see where the markets are heading.
                  </p>
                </div>
              </section>

              {/* Why Turbo proofs */}
              <section className="py-10" id="why-turbo">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="font-creato text-[clamp(24px,2.6vw,32px)] -tracking-[0.01em]">
                    What we offer our partners
                  </h2>
                  <svg className="h-6 w-6 text-zinc-700 dark:text-zinc-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M7 17L17 7" />
                    <path d="M7 7h10v10" />
                  </svg>
                </div>
                <p className="mt-2 max-w-3xl text-sm md:text-base text-zinc-700 dark:text-zinc-300">
                  Six signals partners feel immediately: speed, clarity, production-grade automation, and a crew that hands over leverage on repeat.
                </p>
              </section>

              <section className="pb-12 md:pb-16">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                  {whyTurboProofs.map((proof) => (
                    <ServiceCard
                      key={proof.code}
                      code={proof.code}
                      title={proof.title}
                      subtitle={proof.subtitle}
                      text={proof.text}
                      footnote={proof.footnote}
                    />
                  ))}
                </div>
              </section>
              {/* Showcase-style services (duplicate, visual-heavy) */}
              <ServicesShowcaseSection />

              {/* Interactive services snapshot */}
              <section className="pt-16">
                <InteractiveServicesSection />
              </section>

              {/* Delivery timeline */}
              <section className="pt-16">
                <div className="rounded-3xl border border-zinc-200 bg-white/85 px-6 py-10 shadow-sm backdrop-blur-xl dark:border-zinc-800 dark:bg-zinc-900/85">
                  <TimelineSection
                    timelineRef={timelineRef}
                    customTitle="Deliver as fast as you can give feedback"
                    customDescription="Daily drip updates keep the build in motion—every approval accelerates the next drop."
                    customItems={deliveryTimeline}
                    containerClassName="py-0"
                  />
                </div>
              </section>
            </div>
          </main>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default LandingPage;
